import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { loadStripe, type Stripe } from '@stripe/stripe-js';
import { Loader2Icon } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { useGetClass } from '~entities/classes/api/get-class';
// import { useGetEnrollmentTotalPayments } from '~entities/classes/api/get-enrollment-total-payments';
import {
  getPreviewLineScheduledSuffix,
  type PreviewChargeBreakdown,
} from '~entities/enrollment/api/preview-enrollment';
import { useTransactionFeesSettings } from '~entities/payment/api/fetch-transaction-fees-settings';
import {
  isPaymentMethodAllowed,
  useGetAllowedPaymentMethods,
} from '~entities/payment/api/get-allowed-payment-methods';
import { useGetStudioDetails } from '~entities/studios/api/get-studio-details';
import { useGetWalletBalance } from '~entities/wallet';

import {
  calculateManualEnrollmentDiscount,
  enrollmentConcessionScopeNote,
} from '~features/students/utils/enrollment-summary-discount';

import { useGetCurrency } from '~shared/api/currency-api';
import { formatBillingCycle } from '~shared/constants/tuition-billing-cycle';
import { Button } from '~shared/shadcn/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~shared/shadcn/ui/dialog';

interface EnrollStudentConfirmationProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // studentData: {
  //   name: string;
  //   parentEmail: string;
  // };
  sessionData: {
    label: string;
    value: string;
  } | null;
  classData: {
    label: string;
    value: string;
  } | null;
  discountCategory: null | 'discount' | 'scholarship';
  discountType: 'amount' | 'percentage';
  discountAmount: number;
  discountPercentage: number;
  applyDiscountTo: 'first' | 'all';
  collectUpfront: boolean;
  paymentDeferred: boolean;
  tuitionFee: number;
  finalAmount: number;
  paymentMethod: 'card' | 'us_bank_account' | 'au_becs_debit' | undefined;
  cardSurchargeEligible?: boolean;
  isLoading: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  stripeConfig?: {
    clientSecret: string;
    publishableKey: string;
    stripeAccount?: string;
  } | null;
  perStudentFee: number;
  perFamilyFee: number;
  sessionFeesScheduled?: boolean;
  classFeesScheduled?: boolean;
  isClassFull?: boolean;
  canOverrideClassCapacity?: boolean;
  parentId?: string;
  calculatedDiscountFromAPI?: number;
  /** V2 preview charge for this student — preferred over local fee math when present. */
  previewCharge?: PreviewChargeBreakdown;
  paymentMethodId?: string;
}

const getRuntimeSession = <TSession,>(entity?: { session?: TSession } | null) =>
  entity?.session;

const PaymentForm = ({
  onClose,
  onSuccess,
  disableLink = true,
}: {
  onClose: () => void;
  onSuccess: () => void;
  disableLink?: boolean;
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setIsSubmitting(true);
    try {
      const { error } = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: 'if_required',
      });
      if (error) {
        toast.error(error.message);
      } else {
        toast.success('Payment method added successfully');
        onSuccess();
        onClose();
      }
    } catch (err) {
      console.error('Error in handleSubmit:', err);
      toast.error('Failed to add payment method');
    }
    setIsSubmitting(false);
  };

  return (
    <form onSubmit={handleSubmit} className='space-y-4'>
      <PaymentElement
        options={
          disableLink
            ? ({ wallets: { link: 'never' } } as Record<string, unknown>)
            : undefined
        }
      />
      <div className='flex justify-end gap-2'>
        <Button type='button' variant='outline' onClick={onClose}>
          Cancel
        </Button>
        <Button type='submit' disabled={isSubmitting || !stripe}>
          {isSubmitting ? (
            <Loader2Icon className='animate-spin' />
          ) : (
            'Save Card'
          )}
        </Button>
      </div>
    </form>
  );
};

export const EnrollStudentConfirmation = ({
  open,
  onOpenChange,
  // studentData,
  sessionData,
  classData,
  discountCategory,
  discountType,
  discountAmount,
  discountPercentage,
  applyDiscountTo,
  collectUpfront,
  paymentDeferred,
  tuitionFee,
  finalAmount,
  paymentMethod,
  cardSurchargeEligible,
  isLoading,
  onConfirm,
  onCancel,
  stripeConfig,
  perStudentFee,
  perFamilyFee,
  sessionFeesScheduled = false,
  classFeesScheduled = false,
  isClassFull = false,
  canOverrideClassCapacity = false,
  parentId,
  calculatedDiscountFromAPI = 0,
  previewCharge,
}: EnrollStudentConfirmationProps) => {
  const { data: classDetails } = useGetClass({
    classId: classData?.value ?? '',
  });

  const { data: walletData } = useGetWalletBalance(parentId);

  const { data: transactionFeesSettings } = useTransactionFeesSettings();
  const { data: studioDetails } = useGetStudioDetails();
  const locationId = studioDetails?.locationId ?? '';
  const { data: allowedPaymentMethods } =
    useGetAllowedPaymentMethods(locationId);
  const disableLinkWallet = !isPaymentMethodAllowed(
    allowedPaymentMethods,
    'link',
  );
  const session = getRuntimeSession(classDetails);
  const isHourlyLastMonthCharge =
    classDetails?.tuitionBillingCycle === 'hourly' &&
    !!session?.chargeLastMonthAtCheckout;

  // Get total payments for the selected class
  // const { data: totalPaymentsData } = useGetEnrollmentTotalPayments(
  //   { enrollmentId: classData?.value ?? '' },
  //   { enabled: !!classData?.value },
  // );

  const { data: currencyData } = useGetCurrency();

  const [stripeInstance, setStripeInstance] = useState<Stripe | null>(null);

  useEffect(() => {
    const initStripe = async () => {
      if (stripeConfig?.publishableKey) {
        const stripe = await loadStripe(
          stripeConfig.publishableKey,
          stripeConfig.stripeAccount
            ? { stripeAccount: stripeConfig.stripeAccount }
            : undefined,
        );
        setStripeInstance(stripe);
        if (!stripe) {
          toast.error('Failed to load the payment form');
        }
      }
    };
    void initStripe();
  }, [stripeConfig?.publishableKey, stripeConfig?.stripeAccount]);

  // tuitionFee prop is effectiveTuition from parent, which uses backend-calculated proration
  const proratedTuitionAmount = useMemo(() => {
    if (
      !classDetails ||
      collectUpfront ||
      classDetails.tuitionBillingCycle === 'One Time'
    ) {
      return null;
    }

    if (tuitionFee < classDetails.tuitionFee) {
      return tuitionFee;
    }
    return null;
  }, [classDetails, collectUpfront, tuitionFee]);

  const walletBalance = walletData?.walletBalance ?? 0;
  const walletAmountApplied = useMemo(() => {
    if (previewCharge) {
      return previewCharge.walletCreditApplied;
    }
    return Math.min(walletBalance, finalAmount);
  }, [finalAmount, previewCharge, walletBalance]);
  const walletBefore = walletBalance;
  const walletAfter = Math.max(0, walletBefore - walletAmountApplied);

  // Calculate transaction fee on the full amount before wallet is applied.
  // Prefer V2 preview fee when available.
  const transactionFee = useMemo(() => {
    if (previewCharge) {
      return previewCharge.transactionFeeAmount;
    }

    if (transactionFeesSettings?.passTransactionFeesToCustomers === false) {
      return 0;
    }

    // Check if parent is excluded from transaction fees
    const isParentExcluded = transactionFeesSettings
      ? transactionFeesSettings.excludedParentIds.some(
          (excluded) => excluded.parentId === parentId,
        )
      : false;
    if (isParentExcluded) {
      return 0;
    }

    if (finalAmount <= 0) {
      return 0;
    }

    if (paymentMethod === 'card') {
      if (cardSurchargeEligible === false) {
        return 0;
      }

      // Use settings if available, otherwise use default Stripe rates (2.9% + $0.30)
      const percentageFee =
        parseFloat(transactionFeesSettings?.cardTransactionFees ?? '2.9') / 100;
      const fixedFee = parseFloat(
        transactionFeesSettings?.cardFixedFee ?? '0.30',
      );
      const denominator = 1 - percentageFee;
      if (denominator <= 0) {
        return Math.round((finalAmount * percentageFee + fixedFee) * 100) / 100;
      }
      return (
        Math.round(
          ((finalAmount + fixedFee) / denominator - finalAmount) * 100,
        ) / 100
      );
    } else if (
      paymentMethod === 'us_bank_account' ||
      paymentMethod === 'au_becs_debit'
    ) {
      // Use settings if available, otherwise use default ACH rate (0.8%)
      const percentageFee =
        parseFloat(transactionFeesSettings?.achTransactionFees ?? '0.8') / 100;
      return Math.min(5, Math.round(finalAmount * percentageFee * 100) / 100);
    }

    return 0;
  }, [
    previewCharge,
    transactionFeesSettings,
    parentId,
    finalAmount,
    paymentMethod,
    cardSurchargeEligible,
  ]);

  const finalAmountToCharge = previewCharge
    ? Math.max(0, previewCharge.amountDueWithTransactionFee)
    : Math.max(0, finalAmount + transactionFee - walletAmountApplied);

  const scheduledSuffix = (isScheduled: boolean, amount?: number) =>
    isScheduled && (amount ?? 0) > 0 ? ' (Scheduled)' : '';

  if (!classData || !sessionData) return null;

  const calculateDiscount = () => {
    const registrationFee =
      typeof classDetails?.registrationFeeAmount === 'number'
        ? classDetails.registrationFeeAmount
        : 0;
    const costumeFee =
      typeof classDetails?.costumeFee === 'number'
        ? classDetails.costumeFee
        : 0;

    return calculateManualEnrollmentDiscount({
      discountType,
      discountCategory,
      discountAmount,
      discountPercentage,
      tuitionAmount: tuitionFee,
      otherCharges: registrationFee + costumeFee + perStudentFee + perFamilyFee,
    });
  };

  const concessionScopeNote = enrollmentConcessionScopeNote(discountCategory);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-[500px]'>
        {stripeConfig && stripeInstance ? (
          <Elements
            stripe={stripeInstance}
            options={{ clientSecret: stripeConfig.clientSecret }}
          >
            <PaymentForm
              onClose={onCancel}
              onSuccess={onConfirm}
              disableLink={disableLinkWallet}
            />
          </Elements>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Confirm Enrollment Details</DialogTitle>
              <DialogDescription>
                Please review all details before confirming enrollment.
              </DialogDescription>
            </DialogHeader>

            <div className='grid gap-4 py-4'>
              {isClassFull && (
                <div className='rounded-lg border-2 border-red-500 bg-red-50 p-3'>
                  <p className='text-sm font-semibold text-red-800'>
                    ⚠️ Class is at Capacity
                  </p>
                  <p className='mt-1 text-xs text-red-700'>
                    This class has reached its maximum capacity (
                    {classDetails?.maxSize} students). Current enrollment:{' '}
                    {(classDetails?.maxSize ?? 0) -
                      (classDetails?.remainingSeats ?? 0)}
                    /{classDetails?.maxSize}.{' '}
                    {canOverrideClassCapacity
                      ? 'Your role allows you to override capacity and continue.'
                      : 'Enrollment is not available at this time.'}
                  </p>
                </div>
              )}
              <div className='space-y-4'>
                <div className='grid grid-cols-2 gap-2 text-sm'>
                  {/* <div className='font-medium'>Parent Email:</div>
                  <div>{studentData.parentEmail}</div>

                  <div className='font-medium'>Student Name:</div>
                  <div>{studentData.name}</div> */}

                  <div className='font-medium'>Session:</div>
                  <div>{sessionData.label}</div>

                  <div className='font-medium'>Class Name:</div>
                  <div>{classDetails?.title}</div>

                  {previewCharge ? (
                    <>
                      {previewCharge.lineItems
                        .filter(
                          (line) =>
                            line.code !== 'transaction_fee' &&
                            line.code !== 'wallet_credit',
                        )
                        .map((line, index) => (
                          <div
                            key={`${line.code}-${line.label}-${index}`}
                            className='contents'
                          >
                            <div className='font-medium'>
                              {line.label}
                              {getPreviewLineScheduledSuffix(line)}
                            </div>
                            <div>
                              {currencyData?.symbol}
                              {line.amount.toFixed(2)}
                            </div>
                          </div>
                        ))}

                      {transactionFee > 0 && (
                        <>
                          <div className='font-medium'>Transaction Fee:</div>
                          <div>
                            {currencyData?.symbol}
                            {transactionFee.toFixed(2)}
                          </div>
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      <>
                        <div className='font-medium'>
                          Session Fee per Student:
                        </div>
                        <div>
                          {currencyData?.symbol}
                          {perStudentFee}
                          {scheduledSuffix(sessionFeesScheduled, perStudentFee)}
                        </div>
                      </>
                      <>
                        <div className='font-medium'>
                          Session Fee per Family:
                        </div>
                        <div>
                          {currencyData?.symbol}
                          {perFamilyFee}
                          {scheduledSuffix(sessionFeesScheduled, perFamilyFee)}
                        </div>
                      </>

                      <div className='font-medium'>Costume Fee:</div>
                      <div>
                        {currencyData?.symbol}
                        {classDetails?.costumeFee}
                        {scheduledSuffix(
                          classFeesScheduled,
                          classDetails?.costumeFee,
                        )}
                      </div>

                      <div className='font-medium'>Membership Fee:</div>
                      <div>
                        {currencyData?.symbol}
                        {classDetails?.registrationFeeAmount ?? 0}
                        {scheduledSuffix(
                          classFeesScheduled,
                          classDetails?.registrationFeeAmount,
                        )}
                      </div>

                      <div className='font-medium'>
                        {isHourlyLastMonthCharge
                          ? 'Last Month Hourly Tuition:'
                          : 'Tuition:'}
                      </div>
                      <div>
                        {currencyData?.symbol}
                        {tuitionFee.toFixed(2)}
                        {scheduledSuffix(classFeesScheduled, tuitionFee)}
                      </div>

                      {proratedTuitionAmount !== null && (
                        <>
                          <div className='font-medium text-amber-500'>
                            Prorated Tuition*:
                          </div>
                          <div className='text-amber-500'>
                            {currencyData?.symbol}
                            {proratedTuitionAmount.toFixed(2)}
                          </div>
                        </>
                      )}

                      <div className='font-medium'>
                        {discountCategory === 'scholarship'
                          ? 'Scholarship:'
                          : discountCategory === 'discount'
                            ? 'Discount:'
                            : 'Discount / Scholarship:'}
                      </div>
                      <div>
                        {discountCategory === null
                          ? calculatedDiscountFromAPI > 0
                            ? `(${currencyData?.symbol}${calculatedDiscountFromAPI.toFixed(2)})`
                            : 'None'
                          : discountType === 'percentage'
                            ? `${discountPercentage}% (${currencyData?.symbol}${calculateDiscount().toFixed(2)})`
                            : `(${currencyData?.symbol}${calculateDiscount().toFixed(2)})`}
                      </div>

                      {concessionScopeNote && (
                        <div className='col-span-2 text-xs text-muted-foreground'>
                          {concessionScopeNote}
                        </div>
                      )}

                      {discountCategory !== null &&
                        classDetails?.tuitionBillingCycle !== 'One Time' && (
                          <>
                            <div className='font-medium'>Apply To:</div>
                            <div>
                              {applyDiscountTo === 'first'
                                ? 'First Payment Only'
                                : 'All Payments'}
                            </div>
                          </>
                        )}

                      {transactionFee > 0 && (
                        <>
                          <div className='font-medium'>Transaction Fee:</div>
                          <div>
                            {currencyData?.symbol}
                            {transactionFee.toFixed(2)}
                          </div>
                        </>
                      )}
                    </>
                  )}

                  {(walletBefore > 0 || walletAmountApplied > 0) && (
                    <>
                      <div className='font-medium text-blue-600'>
                        Wallet before:
                      </div>
                      <div className='text-blue-600'>
                        {currencyData?.symbol}
                        {walletBefore.toFixed(2)}
                      </div>
                      {walletAmountApplied > 0 && (
                        <>
                          <div className='font-medium text-emerald-700'>
                            Wallet applied:
                          </div>
                          <div className='text-emerald-700'>
                            -{currencyData?.symbol}
                            {walletAmountApplied.toFixed(2)}
                          </div>
                          <div className='font-medium text-blue-600'>
                            Wallet after:
                          </div>
                          <div className='text-blue-600'>
                            {currencyData?.symbol}
                            {walletAfter.toFixed(2)}
                          </div>
                        </>
                      )}
                    </>
                  )}

                  <div className='font-medium'>Billing Type:</div>
                  <div>
                    {formatBillingCycle(
                      classDetails?.tuitionBillingCycle,
                      classDetails?.billingFrequencyDays,
                    )}
                  </div>

                  <div className='font-medium'>Full Payment Upfront:</div>
                  <div>{collectUpfront ? 'Yes' : 'No'}</div>

                  <div className='font-medium'>Payment Deferred:</div>
                  <div>{paymentDeferred ? 'Yes' : 'No'}</div>

                  {/* {collectUpfront &&
                    classDetails?.tuitionBillingCycle !== 'One Time' && (
                      <>
                        <div className='font-medium'>Total Payments:</div>
                        <div>
                          {totalPaymentsData?.totalPayments ?? 1} payments
                        </div>
                      </>
                    )} */}

                  {paymentMethod && (
                    <>
                      <div className='font-medium'>Payment Method:</div>
                      <div>{paymentMethod}</div>
                    </>
                  )}

                  <div className='col-span-2 mt-2 border-t pt-3 font-medium'>
                    Final Amount to be charged:
                  </div>
                  <div className='col-span-2 text-lg font-bold'>
                    {currencyData?.symbol}
                    {finalAmountToCharge.toFixed(2)}
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant='outline' onClick={onCancel} disabled={isLoading}>
                Cancel
              </Button>
              <Button
                onClick={onConfirm}
                disabled={
                  isLoading || (isClassFull && !canOverrideClassCapacity)
                }
              >
                {isLoading ? (
                  <Loader2Icon className='animate-spin' />
                ) : isClassFull && !canOverrideClassCapacity ? (
                  'Class at Capacity'
                ) : (
                  'Confirm & Enroll'
                )}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
